import React, { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import UpgradeIcon from "@mui/icons-material/Upgrade";

import Prompt from "lib/components/prompt/Prompt";
import ListInfoProvider from "lib/list/ListInfoProvider";
import ComboBox from "lib/ui/textfield/ComboBox";

import FieldTable from "core/field/FieldTable";

import { ScoreResolution } from "evaluation/ScoreResolution";

import { SECTION } from "document/Document";
import DocumentTabs from "document/DocumentTabs";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import { Message } from "utils/feedback/Message";
import { Event } from "utils/history/Event";
import History from "utils/history/History";
import {
	AddAction,
	DeleteAction,
	ReplaceAction
} from "utils/history/PatchAction";
import { ListInfo } from "utils/list/List";

import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";
import FormFieldTable from "core/field/form/FormFieldTable";
import UserAccessOverview from "core/trajectory/access/UserAccessOverview";
import {
	BlueprintInfo,
	BlueprintType,
	getBlueprintInfo,
	getBlueprintsByType,
	Right
} from "../base/BlueprintService";
import { getBlueprintLink } from "../base/Utils";
import { getCompetenceListContent } from "../competencelist/CompetenceListService";
import CompetenceTable from "../competencelist/CompetenceTable";
import EditActionbar from "../competencelist/EditActionbar";
import { getDashboardBlueprintContent } from "../dashboard/DashboardService";
import WidgetTable from "../dashboard/WidgetTable";
import { getInformationBlueprintContent } from "../information/InformationBlueprintService";
import RatingSettings from "./RatingSettings";
import ReportOverview from "./ReportOverview";
import {
	getTrajectoryBlueprintContent,
	TrajectoryBlueprint,
	updateTrajectoryBlueprintContent
} from "./TrajectoryBlueprintService";

const TrajectoryBlueprintView: React.FC = () => {
	const TRAJECTORY_BLUEPRINT = "trajectoryBlueprint";

	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();

	const [info, setInfo] = useState<BlueprintInfo>();
	const [content, setContent] = useState<TrajectoryBlueprint>({
		dashboard: null,
		information: null,
		competenceList: null,
		scoreInfo: [],
		scoreResolution: ScoreResolution.LAST_SCORE,
		reportBlueprints: [],
		roles: [],
		users: []
	});

	const [dashboardListInfo, setDashboardListInfo] = useState<
		ListInfo<string>
	>(ListInfo.init());
	const [criteriaListInfo, setCriteriaListInfo] = useState<ListInfo<string>>(
		ListInfo.init()
	);
	const [reportListInfo, setReportListInfo] = useState<ListInfo<string>>(
		ListInfo.init()
	);

	const [actionHistory, setActionHistory] = useState(new History(content));
	const [message, setMessage] = useState<Message>();

	const [tab, setTab] = useState<SECTION>(SECTION.DASHBOARD);
	const [selectedReport, setSelectedReport] = useState<number>();

	const [competenceListBlueprints, setCompetenceListBlueprints] = useState<
		BlueprintInfo[]
	>([]);
	const [dashboardBlueprints, setDashboardBlueprints] = useState<
		Array<BlueprintInfo>
	>([]);
	const [informationBlueprints, setInformationBlueprints] = useState<
		Array<BlueprintInfo>
	>([]);

	useEffect(loadTrajectoryBlueprint, [id]);

	useEffect(() => {
		getBlueprintsByType(BlueprintType.COMPETENCE_LIST).then(data => {
			data.sort((a, b) => a.name.localeCompare(b.name));
			setCompetenceListBlueprints(data);
		});
		getBlueprintsByType(BlueprintType.DASHBOARD).then(data => {
			data.sort((a, b) => a.name.localeCompare(b.name));
			setDashboardBlueprints(data);
		});
		getBlueprintsByType(BlueprintType.INFORMATION).then(data => {
			data.sort((a, b) => a.name.localeCompare(b.name));
			setInformationBlueprints(data);
		});
	}, []);

	useEffect(() => {
		setDashboardListInfo(prevInfo =>
			prevInfo.withKeys(content.dashboard?.widgets.map(b => b.uuid) ?? [])
		);

		// TODO: own use effect.
		setCriteriaListInfo(prevInfo =>
			prevInfo.withKeys(
				content.competenceList?.competences.map(c => c.uuid) ?? []
			)
		);
	}, [content]);

	useEffect(() => {
		if (selectedReport === undefined) {
			return;
		}

		setReportListInfo(prevInfo =>
			prevInfo.withKeys(
				content.reportBlueprints[selectedReport].fields.map(f => f.uuid)
			)
		);
	}, [selectedReport]);

	useEffect(() => {
		setSelectedReport(undefined);
	}, [content.reportBlueprints]);

	function loadTrajectoryBlueprint() {
		if (!id) {
			return;
		}

		const idValue = Number.parseInt(id, 10);
		if (Number.isNaN(idValue)) {
			setMessage({
				severity: "error",
				text: t("invalidDocumentId", { document: TRAJECTORY_BLUEPRINT })
			});
			return;
		}

		Promise.all([
			getBlueprintInfo(idValue).then(data => setInfo(data)),
			getTrajectoryBlueprintContent(idValue).then(data => {
				setContent(data);
				setActionHistory(new History(data));
			})
		]).catch(onLoadFailure);
	}

	function undo(): void {
		console.assert(actionHistory.hasPrevious());
		if (actionHistory.hasPrevious()) {
			setContent(actionHistory.undo());
		}
	}

	function redo(): void {
		console.assert(actionHistory.hasNext());
		if (actionHistory.hasNext()) {
			setContent(actionHistory.redo());
		}
	}

	function onSaveSuccess(): void {
		setMessage({
			severity: "success",
			text: t("saveDocumentSuccess", { document: TRAJECTORY_BLUEPRINT })
		});
		loadTrajectoryBlueprint();
	}

	function onLoadFailure(): void {
		setMessage({
			severity: "error",
			text: t("loadDocumentError", { document: TRAJECTORY_BLUEPRINT })
		});
	}

	function onSaveFailure(reason: Error): void {
		let errorMessage: string;
		if (reason.message === ERR_INTERNET_DISCONNECTED) {
			errorMessage = t("internetDisconnectedError");
		} else {
			errorMessage = t("saveDocumentFailure", {
				document: TRAJECTORY_BLUEPRINT
			});
		}

		setMessage({
			severity: "error",
			text: errorMessage
		});
	}

	function save(): Promise<void> {
		console.assert(actionHistory !== undefined);
		setMessage(undefined);

		if (!actionHistory.hasPrevious() || !id) {
			return Promise.resolve();
		}

		const actions = actionHistory.getActions();
		const objects = actions.flatMap(action => action.patches());

		const idValue = Number.parseInt(id, 10);
		return updateTrajectoryBlueprintContent(idValue, objects)
			.then(onSaveSuccess)
			.catch(onSaveFailure);
	}

	function close(): void {
		navigate("/blueprints");
	}

	function update(event: Event): void {
		updateWithContext(event, getActiveContext());
	}

	function updateWithContext(event: Event, context: string): void {
		console.assert(actionHistory !== undefined);

		event.applyContext(context);
		const action = event.toAction();
		setContent(actionHistory.apply(action));
	}

	function getActiveContext(): string {
		switch (tab) {
			case SECTION.FIELDS:
				return "/information/fields";
			case SECTION.EVALUATION:
				return "/competenceList";
			case SECTION.REPORTS:
				return "/reportBlueprints/" + selectedReport + "/fields";
			default:
				return "";
		}
	}

	function linkedDashboardText(): JSX.Element {
		const label = t("linkedBlueprint", {
			document: "dashboard"
		});

		if (
			content.dashboard &&
			content.dashboard !== null &&
			content.dashboard.id !== 0
		) {
			const bpId = content.dashboard.id;
			const bpVersion = content.dashboard.version;
			const blueprint = dashboardBlueprints.find(bp => bp.id === bpId);

			if (blueprint === undefined) {
				return (
					<Typography variant="subtitle2">
						{`${label}: ${t("removed")}`};
					</Typography>
				);
			} else {
				return (
					<Grid container>
						<Grid item container columnSpacing={1}>
							<Grid item>
								<Typography variant="subtitle2">
									{label}:
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant="subtitle2">
									<Link
										to={getBlueprintLink(
											bpId,
											BlueprintType.DASHBOARD
										)}>
										{blueprint.name}
									</Link>
								</Typography>
							</Grid>
						</Grid>
						<Grid item container alignItems="center">
							<Grid item>
								<Typography variant="subtitle2">
									{t("version")}: {bpVersion}/
									{blueprint.version}
								</Typography>
							</Grid>
							<Grid item>
								<IconButton
									size="small"
									disabled={bpVersion === blueprint.version}
									onClick={() =>
										changeLinkedDashboard(blueprint)
									}>
									<UpgradeIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				);
			}
		}

		return (
			<Typography variant="subtitle2">
				{`${label}: ${t("none")}`}
			</Typography>
		);
	}

	const changeLinkedDashboard = useCallback(
		(bp: BlueprintInfo | null): void => {
			const path = "/dashboard";
			if (bp === null) {
				const action = new DeleteAction(path, content.dashboard);
				setContent(actionHistory.apply(action));
				return;
			}

			getDashboardBlueprintContent(bp.id).then(data => {
				const newDashboard = {
					id: bp.id,
					version: bp.version,
					widgets: data.widgets
				};

				const action = content.dashboard
					? new ReplaceAction(path, content.dashboard, newDashboard)
					: new AddAction(path, newDashboard);
				setContent(actionHistory.apply(action));
			});
		},
		[actionHistory, content.dashboard]
	);

	function linkedInformationBlueprintText(): JSX.Element {
		const label = t("linkedBlueprint", {
			document: "information"
		});

		if (
			content.information &&
			content.information !== null &&
			content.information.id !== 0
		) {
			const bpId = content.information.id;
			const bpVersion = content.information.version;
			const blueprint = informationBlueprints.find(bp => bp.id === bpId);

			if (blueprint === undefined) {
				return (
					<Typography variant="subtitle2">
						{`${label}: ${t("removed")}`};
					</Typography>
				);
			} else {
				return (
					<Grid container>
						<Grid item container columnSpacing={1}>
							<Grid item>
								<Typography variant="subtitle2">
									{label}:
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant="subtitle2">
									<Link
										to={getBlueprintLink(
											bpId,
											BlueprintType.INFORMATION
										)}>
										{blueprint.name}
									</Link>
								</Typography>
							</Grid>
						</Grid>
						<Grid item container alignItems="center">
							<Grid item>
								<Typography variant="subtitle2">
									{t("version")}: {bpVersion}/
									{blueprint.version}
								</Typography>
							</Grid>
							<Grid item>
								<IconButton
									size="small"
									disabled={bpVersion === blueprint.version}
									onClick={() =>
										changeLinkedInformationBlueprint(
											blueprint
										)
									}>
									<UpgradeIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				);
			}
		}

		return (
			<Typography variant="subtitle2">
				{`${label}: ${t("none")}`}
			</Typography>
		);
	}

	const changeLinkedInformationBlueprint = useCallback(
		(bp: BlueprintInfo | null): void => {
			const path = "/information";
			if (bp === null) {
				const action = new DeleteAction(path, content.information);
				setContent(actionHistory.apply(action));
				return;
			}

			getInformationBlueprintContent(bp.id).then(data => {
				const newInformation = {
					id: bp.id,
					version: bp.version,
					fields: data.fields
				};

				const action = content.information
					? new ReplaceAction(
							path,
							content.information,
							newInformation
					  )
					: new AddAction(path, newInformation);
				setContent(actionHistory.apply(action));
			});
		},
		[actionHistory, content.information]
	);

	function linkedCompetenceListText(): JSX.Element {
		const label = t("linkedBlueprint", {
			document: "competence_list"
		});

		if (
			content.competenceList &&
			content.competenceList !== null &&
			content.competenceList.id !== 0
		) {
			const bpId = content.competenceList.id;
			const bpVersion = content.competenceList.version;
			const blueprint = competenceListBlueprints.find(
				bp => bp.id === bpId
			);

			if (blueprint === undefined) {
				return (
					<Typography variant="subtitle2">
						{`${label}: ${t("removed")}`};
					</Typography>
				);
			} else {
				return (
					<Grid container>
						<Grid item container columnSpacing={1}>
							<Grid item>
								<Typography variant="subtitle2">
									{label}:
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant="subtitle2">
									<Link
										to={getBlueprintLink(
											bpId,
											BlueprintType.COMPETENCE_LIST
										)}>
										{blueprint.name}
									</Link>
								</Typography>
							</Grid>
						</Grid>
						<Grid item container alignItems="center">
							<Grid item>
								<Typography variant="subtitle2">
									{t("version")}: {bpVersion}/
									{blueprint.version}
								</Typography>
							</Grid>
							<Grid item>
								<IconButton
									size="small"
									disabled={bpVersion === blueprint.version}
									onClick={() =>
										changeLinkedCompetenceList(blueprint)
									}>
									<UpgradeIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				);
			}
		}

		return (
			<Typography variant="subtitle2">
				{`${label}: ${t("none")}`}
			</Typography>
		);
	}

	const changeLinkedCompetenceList = useCallback(
		(bp: BlueprintInfo | null): void => {
			const path = "/competenceList";
			if (bp === null) {
				const action = new DeleteAction(path, content.competenceList);
				setContent(actionHistory.apply(action));
				return;
			}

			getCompetenceListContent(bp.id).then(data => {
				const newValue = {
					id: bp.id,
					version: bp.version,
					competences: data.competences
				};

				const action = content.competenceList
					? new ReplaceAction(path, content.competenceList, newValue)
					: new AddAction(path, newValue);
				setContent(actionHistory.apply(action));
			});
		},
		[actionHistory, content.competenceList]
	);

	return (
		<>
			{message && (
				<Alert severity={message.severity}>{message.text}</Alert>
			)}
			<Typography id="title" variant="h1">
				{info?.name}
			</Typography>

			{info &&
				info.sourceBlueprintId !== null &&
				info.sourceBlueprintVersion !== null && (
					<BlueprintLibraryInfo
						id={info.id}
						enabled={info.rights.includes(Right.ADMIN) ?? false}
						blueprintId={info.sourceBlueprintId}
						blueprintVersion={info.sourceBlueprintVersion}
						onUpdate={loadTrajectoryBlueprint}
					/>
				)}

			<EditActionbar
				undoEnabled={actionHistory.hasPrevious()}
				redoEnabled={actionHistory.hasNext()}
				saveEnabled={actionHistory.hasPrevious()}
				onUndo={undo}
				onRedo={redo}
				onSave={save}
				onClose={close}
			/>

			<DocumentTabs activeTab={tab} onSelectTab={setTab} />
			{tab === SECTION.DASHBOARD && (
				<Grid container direction="column">
					<Grid item>{linkedDashboardText()}</Grid>
					<Grid item container>
						<Grid item xs={6} md={4} lg={3}>
							<ComboBox
								options={dashboardBlueprints}
								value={
									dashboardBlueprints.find(
										bp => bp.id === content.dashboard?.id
									) ?? null
								}
								disabled={!info?.rights.includes(Right.WRITE)}
								getOptionLabel={blueprint => blueprint.name}
								getOptionKey={blueprint => blueprint.id}
								onChange={changeLinkedDashboard}
							/>
						</Grid>
					</Grid>
					<Grid>
						<WidgetTable
							elements={content.dashboard?.widgets ?? []}
							listInfo={dashboardListInfo}
							editable={false}
							onSelect={() => {
								throw new Error("Function not supported.");
							}}
							onChange={() => {
								throw new Error("Function not supported.");
							}}
							onInsert={() => {
								throw new Error("Function not supported.");
							}}
						/>
					</Grid>
				</Grid>
			)}

			{tab === SECTION.FIELDS && (
				<Grid container direction="column">
					<Grid item>{linkedInformationBlueprintText()}</Grid>
					<Grid item container>
						<Grid item xs={6} md={4} lg={3}>
							<ComboBox
								options={informationBlueprints}
								value={
									informationBlueprints.find(
										bp => bp.id === content.information?.id
									) ?? null
								}
								disabled={!info?.rights.includes(Right.WRITE)}
								getOptionLabel={blueprint => blueprint.name}
								getOptionKey={blueprint => blueprint.id}
								onChange={changeLinkedInformationBlueprint}
							/>
						</Grid>
					</Grid>
					<Grid>
						<ListInfoProvider
							keys={
								content.information?.fields.map(
									field => field.uuid
								) ?? []
							}
							isEnabled={() => false}>
							<FormFieldTable
								elements={content.information?.fields ?? []}
								editable={false}
								onChange={() => {
									throw new Error("Function not supported.");
								}}
								onInsert={() => {
									throw new Error("Function not supported.");
								}}
							/>
						</ListInfoProvider>
					</Grid>
				</Grid>
			)}

			{tab === SECTION.EVALUATION && (
				<Grid container direction="column" spacing={2}>
					<Grid item>
						{/* No onChangeResolution on purpose: the score
						    resolution selector (#628) stays hidden until its
						    rework, so every template keeps LAST_SCORE, the
						    behaviour from before #628. */}
						<RatingSettings
							scoreInfo={content.scoreInfo}
							disabled={!info?.rights.includes(Right.WRITE)}
							onChange={event =>
								updateWithContext(event, "/scoreInfo")
							}
						/>
					</Grid>

					<Grid item>{linkedCompetenceListText()}</Grid>
					<Grid item container>
						<Grid item xs={6} md={4} lg={3}>
							<ComboBox
								options={competenceListBlueprints}
								value={
									competenceListBlueprints.find(
										bp =>
											bp.id === content.competenceList?.id
									) ?? null
								}
								disabled={!info?.rights.includes(Right.WRITE)}
								getOptionLabel={bp => bp.name}
								getOptionKey={bp => bp.id}
								onChange={changeLinkedCompetenceList}
							/>
						</Grid>
					</Grid>

					<Grid item>
						<CompetenceTable
							elements={content.competenceList?.competences ?? []}
							listInfo={criteriaListInfo}
							editable={false} // Not editable for now.
							onSelect={(keys, select) =>
								setCriteriaListInfo(
									criteriaListInfo.markSelected(keys, select)
								)
							}
							onChange={update}
							onInsert={update}
						/>
					</Grid>
				</Grid>
			)}

			{tab === SECTION.REPORTS && (
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<ReportOverview
							reports={content.reportBlueprints}
							selectedReport={selectedReport}
							enabled={
								info?.rights.includes(Right.WRITE) ?? false
							}
							onSelect={report => setSelectedReport(report)}
							onChange={event =>
								updateWithContext(event, "/reportBlueprints")
							}
						/>
					</Grid>

					{selectedReport !== undefined && (
						<Grid item>
							<FieldTable
								elements={
									content.reportBlueprints[selectedReport]
										?.fields ?? []
								}
								listInfo={reportListInfo}
								editable={false}
								onSelect={(keys, select) =>
									setReportListInfo(
										reportListInfo.markSelected(
											keys,
											select
										)
									)
								}
								onChange={update}
								onInsert={update}
							/>
						</Grid>
					)}
				</Grid>
			)}

			{tab === SECTION.ACCESS && (
				<UserAccessOverview
					roles={content.roles}
					users={content.users}
					information={content.information?.fields ?? []}
					reports={content.reportBlueprints}
					editable={info?.rights.includes(Right.WRITE) ?? false}
					onChange={update}
				/>
			)}

			<Prompt
				when={actionHistory.hasPrevious()}
				message={t("exitDocumentMessage")}
			/>
		</>
	);
};

export default TrajectoryBlueprintView;
